/* eslint-disable @nx/enforce-module-boundaries */
import React, { useMemo, useEffect } from 'react';
import {
  Button,
  Card,
  CardBody,
  HStack,
  Heading,
  MenuItem,
  Box,
  MenuList,
  List,
  ListItem,
  VStack,
  Image,
  Text,
  SimpleGrid,
  Stack,
  CardFooter,
  Container,
  FormControl,
  FormLabel,
  Switch,
  IconButton,
  SeeMore,
  useDisclosure,
} from '@mybridge/ui';
import Link from 'next/link';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import {
  deleteBlog,
  listAllBlogs,
  bookMarkPost,
  blogDetailsSlug,
  removeBookMark,
} from 'v4/store/slices/blogSlice';
import { useToast } from '@mybridge/ui';
import {
  BookMarkPostIcon,
  BookMarkedPostIcon,
  DeleteIcon,
  EditIcon,
} from '@mybridge/icons';
import { updateCampaign } from 'v4/store/actions/campaign.action';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { flattenQueryResults, formatContent, getNextPageFromURL } from 'v4/utils/utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getMyArticlesFunc } from 'v4/store/actions/post.actions';
import { ArticleDeleteModal } from './ArtcleDeleteModal';
import { Tooltip } from '@chakra-ui/react';

const ArticleListBlock = ({ blog, articleUpdated, blogStatus, onArticleDeleted, ...props }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const toast = useToast();
  const { userProfileInfo } = useSelector((state) => state.userProfile);
  const { t } = useTranslator();
  const [selectedBlogId, setSelectedBlogId] = React.useState(null);
  const [isBookmarked, setIsBookmarked] = React.useState(
    blog?.bookmark_user_ids?.includes(userProfileInfo?.id)
  );

  useEffect(() => {
    setIsBookmarked(blog?.bookmark_user_ids?.includes(userProfileInfo?.id));
  }, [blog?.bookmark_user_ids, userProfileInfo?.id]);
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();


  const handleDeleteClick = (blogId) => {
    setSelectedBlogId(blogId);
    onDeleteModalOpen();
  };

  const confirmDelete = () => {
    handleBlogDelete(selectedBlogId);
    onDeleteModalClose();
  };
  
  const handleBlogDelete = (blog) => {
    if (blog) {
      dispatch(deleteBlog(blog)).then((res) => {
        if (res?.payload?.message) {
          toast({
            status:"success",
            position:"top",
            title: res.payload.message,
          });
          if (onArticleDeleted) {
            onArticleDeleted(true);
          }
        }

      });
    }
  };

  // const handleBlogBookMark = (blog) => {
  //   dispatch(bookMarkPost(blog)).then((res) => {
  //     if (res.payload.readers) {
  //       toast({
  //         status:"success",
  //         position:"top",
  //         description: t('toast.articleBookmarked') || 'Article bookmarked!',
  //       });
  //     }
  //       refetchingArticles();
  //     // dispatch(getMyArticlesFunc);
  //   });
  // };

  const {
      data: myArticles,
      isFetching: articlesListFetching,
      isLoading: articlesListLoading,
      refetch: refetchingArticles,
      fetchNextPage: isFetchingNextPage,
      hasNextPage: fetchNextPage,
    } = useInfiniteQuery({
      queryKey: ['getMyArticlesFunc', { status: 2, include_bookmarks: true }],
      queryFn: async ({ pageParam, queryKey }) => {
        const resp = (
          await dispatch(
            getMyArticlesFunc({ pageParam, status: queryKey[1]?.status, include_bookmarks: queryKey[1]?.include_bookmarks })
          )
        )?.payload;
        return resp;
      },
      getNextPageParam: (lastPage) => getNextPageFromURL(lastPage?.next),
      refetchOnWindowFocus: false,
    });
    const handleBlogBookMark = (blogDetailsId) => {
      setIsBookmarked(true);
      dispatch(bookMarkPost(blogDetailsId)).then((res) => {
        if (res.payload.readers) {
          toast?.({
            title: t('toast.articleBookmarked') || 'Article bookmarked!',
            status: 'success',
            position: 'top',
          });
        }
        
        // Refetch the articles list to update bookmarks
        refetchingArticles();
        
        // Fetch the updated blog details with the correct slug
        if (blog?.slug) {
          dispatch(blogDetailsSlug(blog.slug));
        }
      }).catch(() => {
        setIsBookmarked(false);
      });
    };
  
    const handleRemoveBlogBookMark = (blogDetailsId) => {
      setIsBookmarked(false);
      dispatch(removeBookMark(blogDetailsId)).then((res) => {
        if (res.payload.readers) {
          toast?.({
            title: 'Bookmark removed!',
            status: 'success',
            position: 'top',
          });
        }
        
        // Refetch the articles list to update bookmarks
        refetchingArticles();
        
        // Fetch the updated blog details with the correct slug
        if (blog?.slug) {
          dispatch(blogDetailsSlug(blog.slug));
        }
      }).catch(() => {
        setIsBookmarked(true);
      });
    };

  const handleProfileShowCheck = (e) => {
    const value = e.target.checked;
    const payload = {
      campaign_id: blog?.id,
      add_to_profile: value,
    };
    dispatch(updateCampaign(payload)).then((res) => {
      if (res) {
        articleUpdated(true);
      }
    });
  };

  // Enhanced image extraction with size calculation
  const extractAndProcessImages = (htmlString) => {
    if (!htmlString) return { images: [], textContent: '' };
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const images = doc.querySelectorAll('img');
    const textContent = doc.body.textContent || doc.body.innerText || '';
    
    const processedImages = Array.from(images).map((img, index) => ({
      id: index,
      src: img.src,
      alt: img.alt || `Image ${index + 1}`,
      originalWidth: img.width || null,
      originalHeight: img.height || null
    }));

    return {
      images: processedImages,
      textContent: textContent.replace(/\s+/g, ' ').trim()
    };
  };

  const { images: descriptionImages, textContent } = useMemo(() => 
    extractAndProcessImages(blog?.description), 
    [blog?.description]
  );

  // Calculate display configuration based on number of images
  const getImageDisplayConfig = (imageCount) => {
    if (imageCount === 1) return { columns: 1, maxHeight: "120px" };
    if (imageCount === 2) return { columns: 2, maxHeight: "80px" };
    if (imageCount >= 3) return { columns: 2, maxHeight: "60px" };
    return { columns: 1, maxHeight: "100px" };
  };

  const displayConfig = getImageDisplayConfig(descriptionImages.length);

  // Handle hashtag and mention link clicks for search navigation
  useEffect(() => {
    const handleHashtagClick = (e) => {
      const target = e.target.closest('.hashtag-link');
      if (target) {
        e.preventDefault();
        e.stopPropagation();
        const hashtag = target.getAttribute('data-hashtag');
        if (hashtag) {
          router.push(`/g/?query=${encodeURIComponent(hashtag)}`);
        }
      }
    };

    const handleMentionClick = (e) => {
      const target = e.target.closest('.mention-link');
      if (target) {
        e.preventDefault();
        e.stopPropagation();
        const mentionSlug = target.getAttribute('data-mention-slug');
        if (mentionSlug) {
          router.push(`/p/${mentionSlug}`, undefined, { 
            shallow: true,
            scroll: false
          });
        }
      }
    };

    document.addEventListener('click', handleHashtagClick, true);
    document.addEventListener('click', handleMentionClick, true);
    
    return () => {
      document.removeEventListener('click', handleHashtagClick, true);
      document.removeEventListener('click', handleMentionClick, true);
    };
  }, [router]);

  return (
    <Card
      border="0px solid #D9D9D9"
      borderRadius="16px"
      overflow="hidden"
      boxShadow="0px 2px 6px 0px #00000033"
      h="480px"
      background="gray.200"
      minWidth={["342px","full","full","364px"]}
      {...props}
    >
     { blog.cover_image&&<Image src={blog.cover_image} w="100%" h="200px" objectFit="cover" alt="" />}
      <CardBody p={3}>
        <Stack spacing="1" mb={3}>
          <Link href={`/articles/detail/${blog?.id || blog?.slug}`}>
            <Heading
              color="brandPrimary.500"
              fontSize="16px"
              fontWeight="500"
              cursor="pointer"
              mb={0}
              maxH="58px"
              overflow="hidden"
            >
              {blog?.title}
              {/* <SeeMore maxLength={70} text= isInline={true} /> */}
            </Heading>
          </Link>
          <Text fontSize="14px" color="brandPrimary.500">
          {t('notification.by') || "By"}
            {' '}
            {blog?.company?.id
              ? `${blog?.company?.name}, ${
                  blog?.company?.city
                    ? blog?.company?.city + ', ' + blog?.company?.country
                    : blog?.company?.country
              }`
              : `${blog?.author?.display_name} ${blog?.company?.name??""}`}
          </Text>
        </Stack>
        {/* <Text fontSize="14px" color="brandGray.700">
          {blog?.excerpt}
        </Text> */}
        <Box fontSize="14px" color="brandGray.700" maxH="150px" overflow="hidden">
          {/* Display images from description */}
          {/* {descriptionImages.length > 0 && (
            <Box mb={2}>
              <SimpleGrid columns={displayConfig.columns} spacing={1}>
                {descriptionImages.slice(0, 4).map((image) => (
                  <Box key={image.id} position="relative">
                    <Image
                      src={image.src}
                      alt={image.alt}
                      borderRadius="6px"
                      objectFit="cover"
                      maxH={displayConfig.maxHeight}
                      w="100%"
                      fallback={
                        <Box
                          bg="gray.200"
                          borderRadius="6px"
                          h={displayConfig.maxHeight}
                          w="100%"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Text fontSize="xs" color="gray.500">Image</Text>
                        </Box>
                      }
                    />
                    
                    {image.id === 3 && descriptionImages.length > 4 && (
                      <Box
                        position="absolute"
                        top="0"
                        left="0"
                        right="0"
                        bottom="0"
                        bg="blackAlpha.600"
                        borderRadius="6px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="white" fontSize="sm" fontWeight="bold">
                          +{descriptionImages.length - 4}
                        </Text>
                      </Box>
                    )}
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          )} */}

          {/* Display text content */}
          {/* <Text noOfLines={descriptionImages.length > 0 ? 2 : 4}>
            {blog?.excerpt || textContent.substring(0, 200) + (textContent.length > 200 ? '...' : '')}
          </Text> */}
          <Box 
            fontSize="14px" 
            color="brandGray.700" 
            noOfLines={4} 
            dangerouslySetInnerHTML={{
              __html: formatContent(blog?.description || '', blog?.mention_users || [])
            }}
            // dangerouslySetInnerHTML={{ __html: blog?.excerpt || '' }}
            sx={{
              ul: { listStyleType: 'disc', pl: 5, my: 2 },
              ol: { listStyleType: 'decimal', pl: 5, my: 2 },
              li: { display: 'list-item', mb: 1 },
              p: { mb: 2 },
              h1: { fontSize: 'xl', fontWeight: 'bold', my: 2 },
              h2: { fontSize: 'lg', fontWeight: 'bold', my: 2 },
              h3: { fontSize: 'md', fontWeight: 'bold', my: 2 },
            }}
          />
        </Box>
      </CardBody>
      <CardFooter pt={0} p={3}>
        <HStack justifyContent="space-between" w="100%">
          {blog?.author?.id === userProfileInfo?.id &&
          blogStatus === 'published' ? (
            <FormControl display="flex" alignItems="center">
              <Switch
                id="add_to_profile"
                defaultChecked={blog?.add_to_profile}
                me={3}
                size="sm"
                onChange={(e) => handleProfileShowCheck(e)}
              />
              <FormLabel
                htmlFor="add_to_profile"
                mb="0"
                fontSize="12px"
                fontWeight="400"
              >
                {t('notification.addToProfile') || "Add to profile"}
              </FormLabel>
            </FormControl>
          ) : (
            <Box as="span"></Box>
          )}
          <HStack>
            {blog?.author?.id === userProfileInfo?.id ? (
              <>
                {/* <Button
                  onClick={() => router.push(`/articles/create/${blog?.id}`)}
                >
                  Edit
                </Button> */}
                {/* <Link href={`/articles/create/${blog?.id}`}>
                  <IconButton icon={<EditIcon />} />
                </Link> */}
                <Link href={`/articles/create/${blog?.id}`}>
                  <Button
                    p={0}
                    variant="transparent"
                    _hover={{
                        bg: "gray.100",
                        color: "brandPrimary.500"
                      }}
                  >
                    <EditIcon />
                  </Button>
                </Link>
                
                <Button
                  p={0}
                  variant="transparent"
                  onClick={() => handleDeleteClick(blog?.id)}
                  _hover={{
                      bg: "gray.100",
                      color:"brandRed.500"
                    }}
        
                >
                  <DeleteIcon/>
                </Button>
                
                <ArticleDeleteModal
                  isOpen={isDeleteModalOpen}
                  onClose={onDeleteModalClose}
                  onConfirm={confirmDelete}
                />
              </>
            ) : (
              <></>
            )}
          </HStack>
          {blog?.author?.id !== userProfileInfo?.id && (
            <HStack>
              {blog?.blog_type === 3 ? (
              
                <Text color="brandGreen.500">{t('notification.admin') || "Admin"}</Text>
              ) : (
                isBookmarked ? 
                (
                    <Tooltip label={'Remove Bookmark'}>
                      <IconButton
                        variant="secondary"
                        icon={<BookMarkedPostIcon />}
                        onClick={() => handleRemoveBlogBookMark(blog?.id)}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip label={'Bookmark'}>
                      <IconButton
                        variant="secondary"
                        icon={<BookMarkPostIcon />}
                        onClick={() => handleBlogBookMark(blog?.id)}
                      />
                    </Tooltip>
                  )
              )}
            </HStack>
          )}
        </HStack>
      </CardFooter>
    </Card>
  );
};

export default ArticleListBlock;
